import { TabbedCard, TabbedCardContent, TabbedCardOrientation } from "@ucl/library";
import { BasicInformationForm } from "../BasicInformation";
import { KeyPeople } from "../KeyPeople/KeyPeople";
import { OrganizationInfoForm } from "../OrganizationInfo";
import {
  FinancialApplicationFormSectionContextType,
  FinancialApplicationFormValidationDisplayBehavior,
} from "@/hooks/useFinancialApplicationFormSectionValidation";
import { JointAccountOwners } from "../JointAccountOwners/JointAccountOwners";
import { PersonalAccountBeneficiaries } from "../PersonalAccountBeneficiaries/PersonalAccountBeneficiaries";
import { FinancialProductMultiSelectForm } from "../FinancialProductMultiSelectForm/FinancialProductMultiSelectForm";
import { OptIns } from "../OptIns/OptIns";
// import { LawFirmOrganizationAccountRiskReviewForm } from "../LawFirmOrganizationAccountRiskReviewForm/LawFirmOrganizationAccountRiskReviewForm";
// import { NonLawFirmOrganizationAccountRiskReviewForm } from "../NonLawFirmOrganizationAccountRiskReviewForm/NonLawFirmOrganizationAccountRiskReviewForm";
import { OrganizationAcceptableUseForm } from "../OrganizationAcceptableUse";
import { PersonalAccountAcceptableUseForm } from "../PersonalAccountAcceptableUse/PersonalAccountAcceptableUseForm";
// import { PersonalAccountRiskReviewForm } from "../PersonalAccountRiskReviewForm/PersonalAccountRiskReviewForm";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";
import { FinancialApplicationSection } from "@/types/enums";
import { BankerValidationErrorsBadge } from "./BankerValidationErrorsBadge";
import { bankerApplicationViewStateAtomFamily } from "@/atoms/bankerApplicationViewStateAtomFamily";
import { useAtom } from "jotai";
import { ValidationErrorDisplayMode } from "../ValidationErrorsCounts/ValidationErrorsCounts";
import "./BankerApplicationDetailsForm.scss";
import { AccountDetailsForm } from "../AccountDetails/AccountDetailsForm";
import { APPLICATION_NAVIGATION_ITEMS } from "@/types/ApplicationNavigationItems";
import { FinancialApplicationDocumentsForm } from "../FinancialApplicationDocuments/FinancialApplicationDocumentsForm";

export interface BankerApplicationDetailsFormProps {
  financialApplicationId: string;
}

export const BankerApplicationDetailsForm: React.FC<BankerApplicationDetailsFormProps> = ({
  financialApplicationId,
}) => {
  const { conditionalFlags } = useFinancialApplicationCoreData(financialApplicationId);
  const [bankerApplicationViewStateAtom] = useAtom(
    bankerApplicationViewStateAtomFamily(financialApplicationId)
  );

  const [activeTabKey, setActiveTabKey] = useAtom(bankerApplicationViewStateAtom.activeTabKey);

  const validationsPopoverClassName = "banker-application-details-form__popover";
  const validationsDisplayMode = ValidationErrorDisplayMode.Remaining;
  const tabsData: TabbedCardContent[] = [
    {
      tabKey: "product-details",
      headerContent: (
        <span>
          {APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.ProductSelection]?.label}{" "}
          <BankerValidationErrorsBadge
            financialApplicationId={financialApplicationId}
            section={FinancialApplicationSection.ProductSelection}
            popoverClassName={validationsPopoverClassName}
            displayMode={validationsDisplayMode}
          />
        </span>
      ),
      tabContent: (
        <FinancialProductMultiSelectForm financialApplicationId={financialApplicationId} />
      ),
    },
    {
      tabKey: "client-details",
      headerContent: (
        <span>
          {
            APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.PersonInformationBasicInfo]
              ?.label
          }{" "}
          <BankerValidationErrorsBadge
            financialApplicationId={financialApplicationId}
            section={FinancialApplicationSection.PersonInformationBasicInfo}
            popoverClassName={validationsPopoverClassName}
            displayMode={validationsDisplayMode}
          />
        </span>
      ),
      tabContent: (
        <BasicInformationForm
          financialApplicationId={financialApplicationId}
          autoValidationBehavior={
            FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
          }
          validationContextType={FinancialApplicationFormSectionContextType.Banker}
          shouldShowDigitalBankingFields={true}
          shouldShowDebitCardFields={true}
        />
      ),
    },
    {
      tabKey: "account-details",
      headerContent: (
        <span>
          {APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.AccountDetails]?.label}{" "}
          <BankerValidationErrorsBadge
            financialApplicationId={financialApplicationId}
            section={FinancialApplicationSection.AccountDetails}
            popoverClassName={validationsPopoverClassName}
            displayMode={validationsDisplayMode}
          />
        </span>
      ),
      tabContent: (
        <AccountDetailsForm
          financialApplicationId={financialApplicationId}
          autoValidationBehavior={
            FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
          }
          validationContextType={FinancialApplicationFormSectionContextType.Banker}
        />
      ),
      disabled: !conditionalFlags.isAccountDetailsEnabled,
    },
    {
      tabKey: "joint-account-owners",
      headerContent: (
        <span>
          {APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.JointAccountOwners]?.label}{" "}
          <BankerValidationErrorsBadge
            financialApplicationId={financialApplicationId}
            section={FinancialApplicationSection.JointAccountOwners}
            popoverClassName={validationsPopoverClassName}
            displayMode={validationsDisplayMode}
          />
        </span>
      ),
      tabContent: (
        <div className="joint-account-owners-info">
          <JointAccountOwners
            financialApplicationId={financialApplicationId}
            initiallyCollapseJointAccountOwnerForms={true}
            validationBehavior={
              FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
            }
            validationContextType={FinancialApplicationFormSectionContextType.Banker}
            shouldAskIfCanProvideGovernmentIdInformation={true}
            shouldAlwaysShowGovernmentIdInformationFields={true}
            shouldShowDigitalBankingFields={true}
            shouldShowDebitCardFields={true}
          />
        </div>
      ),
      disabled: !conditionalFlags.isJointAccountOwnersEnabled,
    },
    {
      tabKey: "personal-account-beneficiaries",
      headerContent: (
        <span>
          {
            APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.PersonalAccountBeneficiaries]
              ?.label
          }{" "}
          <BankerValidationErrorsBadge
            financialApplicationId={financialApplicationId}
            section={FinancialApplicationSection.PersonalAccountBeneficiaries}
            popoverClassName={validationsPopoverClassName}
            displayMode={validationsDisplayMode}
          />
        </span>
      ),
      tabContent: (
        <div className="personal-account-beneficiaries-info">
          <PersonalAccountBeneficiaries
            financialApplicationId={financialApplicationId}
            initiallyCollapsePersonalAccountBeneficiaryForms={true}
            validationBehavior={
              FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
            }
            validationContextType={FinancialApplicationFormSectionContextType.Banker}
          />
        </div>
      ),
      disabled: !conditionalFlags.isPersonalAccountBeneficiariesEnabled,
    },
    {
      tabKey: "organization-info",
      headerContent: (
        <span>
          {APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.OrganizationInformation]?.label}{" "}
          <BankerValidationErrorsBadge
            financialApplicationId={financialApplicationId}
            section={FinancialApplicationSection.OrganizationInformation}
            popoverClassName={validationsPopoverClassName}
            displayMode={validationsDisplayMode}
          />
        </span>
      ),
      tabContent: (
        <OrganizationInfoForm
          financialApplicationId={financialApplicationId}
          autoValidationBehavior={
            FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
          }
          validationContextType={FinancialApplicationFormSectionContextType.Banker}
        />
      ),
      disabled: !conditionalFlags.isOrganizationInfoEnabled,
    },
    {
      tabKey: "key-people",
      headerContent: (
        <span>
          {APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.KeyPeople]?.label}{" "}
          <BankerValidationErrorsBadge
            financialApplicationId={financialApplicationId}
            section={FinancialApplicationSection.KeyPeople}
            popoverClassName={validationsPopoverClassName}
            displayMode={validationsDisplayMode}
          />
        </span>
      ),
      tabContent: (
        <div className="key-people-info">
          <KeyPeople
            financialApplicationId={financialApplicationId}
            initiallyCollapseKeyPersonForms={true}
            validationBehavior={
              FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
            }
            validationContextType={FinancialApplicationFormSectionContextType.Banker}
            shouldAskIfCanProvideGovernmentIdInformation={true}
            shouldAlwaysShowGovernmentIdInformationFields={true}
            shouldShowDigitalBankingFields={true}
            shouldShowDebitCardFields={true}
          />
        </div>
      ),
      disabled: !conditionalFlags.isKeyPeopleEnabled,
    },
    {
      tabKey: "personal-acceptable-use",
      headerContent: (
        <span>
          {
            APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.PersonalAccountAcceptableUse]
              ?.label
          }{" "}
          <BankerValidationErrorsBadge
            financialApplicationId={financialApplicationId}
            section={FinancialApplicationSection.PersonalAccountAcceptableUse}
            popoverClassName={validationsPopoverClassName}
            displayMode={validationsDisplayMode}
          />
        </span>
      ),
      tabContent: (
        <PersonalAccountAcceptableUseForm
          financialApplicationId={financialApplicationId}
          autoValidationBehavior={
            FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
          }
          validationContextType={FinancialApplicationFormSectionContextType.Banker}
        />
      ),
      disabled: !conditionalFlags.isPersonalAccountAcceptableUseEnabled,
    },
    {
      tabKey: "organization-acceptable-use",
      headerContent: (
        <span>
          {
            APPLICATION_NAVIGATION_ITEMS[
              FinancialApplicationSection.OrganizationAccountAcceptableUse
            ]?.label
          }{" "}
          <BankerValidationErrorsBadge
            financialApplicationId={financialApplicationId}
            section={FinancialApplicationSection.OrganizationAccountAcceptableUse}
            popoverClassName={validationsPopoverClassName}
            displayMode={validationsDisplayMode}
          />
        </span>
      ),
      tabContent: (
        <OrganizationAcceptableUseForm
          financialApplicationId={financialApplicationId}
          autoValidationBehavior={
            FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
          }
          validationContextType={FinancialApplicationFormSectionContextType.Banker}
        />
      ),
      disabled: !conditionalFlags.isOrganizationAcceptableUseEnabled,
    },
    {
      tabKey: "opt-ins",
      headerContent: (
        <span>
          {APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.OptIns]?.label}{" "}
          <BankerValidationErrorsBadge
            financialApplicationId={financialApplicationId}
            section={FinancialApplicationSection.OptIns}
            popoverClassName={validationsPopoverClassName}
            displayMode={validationsDisplayMode}
          />
        </span>
      ),
      tabContent: (
        <div className="opt-ins-info">
          <OptIns
            financialApplicationId={financialApplicationId}
            validationBehavior={
              FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
            }
            initiallyCollapsed
            readOnly={false}
            showCdars
            showInsuredCashSweep
            validationContextType={FinancialApplicationFormSectionContextType.Banker}
          />
        </div>
      ),
    },
    {
      tabKey: "financial-application-documents",
      headerContent: (
        <span>
          {
            APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.FinancialApplicationDocuments]
              ?.label
          }
        </span>
      ),
      tabContent: (
        <FinancialApplicationDocumentsForm
          financialApplicationId={financialApplicationId}
          autoValidationBehavior={
            FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
          }
        />
      ),
    },
    // {
    //   tabKey: "personal-account-risk-review",
    //   headerContent: (
    //     <span>
    //       {
    //         APPLICATION_NAVIGATION_ITEMS[FinancialApplicationSection.PersonalAccountRiskReview]
    //           ?.label
    //       }{" "}
    //       <BankerValidationErrorsBadge
    //         financialApplicationId={financialApplicationId}
    //         section={FinancialApplicationSection.PersonalAccountRiskReview}
    //         popoverClassName={validationsPopoverClassName}
    //         displayMode={validationsDisplayMode}
    //       />
    //     </span>
    //   ),
    //   tabContent: (
    //     <PersonalAccountRiskReviewForm
    //       financialApplicationId={financialApplicationId}
    //       autoValidationBehavior={
    //         FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
    //       }
    //       validationContextType={FinancialApplicationFormSectionContextType.Banker}
    //     />
    //   ),
    //   disabled: !conditionalFlags.isPersonalAccountRiskReviewEnabled,
    // },
    // {
    //   tabKey: "law-firm-organization-account-risk-review",
    //   headerContent: (
    //     <span>
    //       {
    //         APPLICATION_NAVIGATION_ITEMS[
    //           FinancialApplicationSection.LawFirmOrganizationAccountRiskReview
    //         ]?.label
    //       }{" "}
    //       <BankerValidationErrorsBadge
    //         financialApplicationId={financialApplicationId}
    //         section={FinancialApplicationSection.LawFirmOrganizationAccountRiskReview}
    //         popoverClassName={validationsPopoverClassName}
    //         displayMode={validationsDisplayMode}
    //       />
    //     </span>
    //   ),
    //   tabContent: (
    //     <LawFirmOrganizationAccountRiskReviewForm
    //       financialApplicationId={financialApplicationId}
    //       autoValidationBehavior={
    //         FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
    //       }
    //       validationContextType={FinancialApplicationFormSectionContextType.Banker}
    //     />
    //   ),
    //   disabled: !conditionalFlags.isLawFirmOrganizationAccountRiskReviewEnabled,
    // },
    // {
    //   tabKey: "non-law-firm-organization-account-risk-review",
    //   headerContent: (
    //     <span>
    //       {
    //         APPLICATION_NAVIGATION_ITEMS[
    //           FinancialApplicationSection.NonLawFirmOrganizationAccountRiskReview
    //         ]?.label
    //       }{" "}
    //       <BankerValidationErrorsBadge
    //         financialApplicationId={financialApplicationId}
    //         section={FinancialApplicationSection.NonLawFirmOrganizationAccountRiskReview}
    //         popoverClassName={validationsPopoverClassName}
    //         displayMode={validationsDisplayMode}
    //       />
    //     </span>
    //   ),
    //   tabContent: (
    //     <NonLawFirmOrganizationAccountRiskReviewForm
    //       financialApplicationId={financialApplicationId}
    //       autoValidationBehavior={
    //         FinancialApplicationFormValidationDisplayBehavior.AlwaysDisplayValidationErrors
    //       }
    //       validationContextType={FinancialApplicationFormSectionContextType.Banker}
    //     />
    //   ),
    //   disabled: !conditionalFlags.isNonLawFirmOrganizationAccountRiskReviewEnabled,
    // },
  ];

  return (
    <div className="banker-application-details-form">
      <TabbedCard
        tabs={tabsData}
        orientation={TabbedCardOrientation.Vertical}
        className=""
        overflowMode="Popup"
        activeTabKey={activeTabKey}
        onTabChange={(tabKey) => setActiveTabKey(tabKey)}
        // TODO: Uncomment this when we fix the rendering issue
        // basePath={"/banker/applications/" + financialApplicationId + "/details"}
      />
    </div>
  );
};
